import { uniqBy } from "lodash-es";

import Visibility from "@mapmycustomers/shared/enum/Visibility";
import { FieldDescription } from "@mapmycustomers/shared/types/entity";
import { MapPersistedEntityColumns } from "@mapmycustomers/shared/types/map/types";
import Team from "@mapmycustomers/shared/types/Team";

import { displayOrderComparator } from "@app/util/sorting";

import { InternalFieldDescription } from "../EntityColumnsConfiguration";
import { ListRef, toPersistedKey } from "../ListRef";

import convertToFieldDescription, { ResolveField } from "./convertToFieldDescription";
import readPersistedList from "./readPersistedList";

export interface PinnedLevelSources {
  org?: Partial<MapPersistedEntityColumns>;
  teams: Pick<Team, "id" | "metaData">[];
  user?: Partial<MapPersistedEntityColumns>;
}

interface CombineOptions {
  isOwner: boolean;
  resolveField: ResolveField;
}

// Combines one list from the org, team and user levels. A team row is dropped when the org pins
// the field, a user row when the org or a team does. Org and team rows are locked for non-owners.
const combinePinnedList = (
  ref: ListRef,
  { org, teams, user }: PinnedLevelSources,
  { isOwner, resolveField }: CombineOptions
): InternalFieldDescription[] => {
  const { entityType } = ref;
  const resolve = (fieldName: string) => resolveField(entityType, fieldName);
  // Legacy flat entries are numbered from startFrom; only the shared lists can have them
  const readDescriptions = (
    mapConfiguration: Partial<MapPersistedEntityColumns> | undefined,
    startFrom: number
  ): FieldDescription[] =>
    (
      readPersistedList(mapConfiguration, toPersistedKey(ref), {
        flatFallback: ref.kind === "shared",
      }) ?? []
    ).map(convertToFieldDescription(resolveField, entityType, startFrom));

  const orgRows: InternalFieldDescription[] = uniqBy(readDescriptions(org, 0), "fieldName").flatMap(
    ({ displayOrder, fieldName }) => {
      const field = resolve(fieldName);
      return field
        ? [
            {
              disabled: !isOwner,
              displayOrder,
              field,
              visibility: Visibility.SHARED_WITH_ORGANIZATION,
            },
          ]
        : [];
    }
  );
  const orgFields = orgRows.map(({ field }) => field);

  // A field pinned by several teams keeps the first team's entry
  const teamStartFrom = orgRows.at(-1)?.displayOrder ?? 0;
  const teamDescriptions = teams.reduce<FieldDescription[]>(
    (result, { metaData }) =>
      uniqBy(
        [...result, ...readDescriptions(metaData?.mapConfiguration, teamStartFrom + result.length)],
        "fieldName"
      ),
    []
  );
  const teamRows: InternalFieldDescription[] = teamDescriptions.flatMap(
    ({ displayOrder, fieldName }) => {
      const field = resolve(fieldName);
      if (!field || orgFields.includes(field)) {
        return [];
      }
      const teamIds = teams
        .filter(({ metaData }) =>
          readDescriptions(metaData?.mapConfiguration, 0).some(
            ({ fieldName: name }) => name === fieldName
          )
        )
        .map(({ id }) => id);
      return [
        {
          disabled: !isOwner,
          displayOrder,
          field,
          teamIds,
          visibility: Visibility.SHARED_WITH_TEAM,
        },
      ];
    }
  );
  const teamFields = teamRows.map(({ field }) => field);

  const userRows: InternalFieldDescription[] = uniqBy(
    readDescriptions(user, teamRows.at(-1)?.displayOrder ?? 0),
    "fieldName"
  )
    .flatMap(({ displayOrder, fieldName }): InternalFieldDescription[] => {
      const field = resolve(fieldName);
      return field && !orgFields.includes(field) && !teamFields.includes(field)
        ? [{ disabled: false, displayOrder, field, visibility: Visibility.PRIVATE }]
        : [];
    })
    .sort(displayOrderComparator);

  // An owner sees every row in order; anyone else sees personal rows below the ones set for them
  return [...orgRows, ...teamRows, ...(isOwner ? userRows : [])]
    .sort(displayOrderComparator)
    .concat(isOwner ? [] : userRows);
};

export default combinePinnedList;
